// src/services/costs/zahlungsanweisungPdf.js
// Erzeugt das Deckblatt einer Zahlungsanweisung (US-KOST-022, ADR 0054) als PDF
// — clientseitig mit jsPDF, ohne Server. Die Funktion ist bewusst rein: sie
// bekommt einen vollstaendigen Daten-Snapshot (`zaDaten`, siehe
// ZahlungsanweisungModal) und rendert daraus das Dokument. Derselbe Snapshot
// wird auf der Zahlung gespeichert (costEstimatePayments.ZaDaten), damit das
// PDF spaeter unveraendert erneut heruntergeladen werden kann.
//
// Vorlage: localFiles/local/Beispiel_014_260506_Zahlungsanweisung_1_GP.pdf
// (A4 hoch, Logo oben rechts, Adressat links / Auftraggeberin rechts,
// Titelzeile, Unternehmer, Auftrag/Vertrag Nr., Zahlungsuebersicht mit
// Prozenten, MWST-Aufschluesselung, Unterschriften, Absender-Fusszeile).

import { loadJsPdf } from "../../utils/loadJsPdf.js";
import { formatSwissCurrency } from "./controlCalculations.js";
import {
  MF_FIRMA,
  MF_ABSENDER,
  LOGO_RATIO,
  LEFT,
  RIGHT,
  LH,
  loadLogoDataUrl,
  createDrawHelpers,
} from "../../utils/pdfTemplate.js";

// Re-Export: bestehende Importstellen von MF_FIRMA/MF_ABSENDER aus dieser
// Datei bleiben dadurch funktionsfaehig (siehe src/utils/pdfTemplate.js).
export { MF_FIRMA, MF_ABSENDER };

const MID = 120; // linke Kante der rechten Spalte

const MONATE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const parseIso = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
};

// "02.09.2026"
export function fmtDatum(iso) {
  const p = parseIso(iso);
  if (!p) return iso || "";
  return `${String(p.d).padStart(2, "0")}.${String(p.m).padStart(2, "0")}.${p.y}`;
}

// "26. August 2026"
export function fmtDatumLang(iso) {
  const p = parseIso(iso);
  if (!p) return iso || "";
  return `${p.d}. ${MONATE[p.m - 1] || ""} ${p.y}`;
}

// "260902" fuer Dateinamen
function fmtDatumKurz(iso) {
  const p = parseIso(iso);
  if (!p) return "";
  return `${String(p.y).slice(-2)}${String(p.m).padStart(2, "0")}${String(p.d).padStart(2, "0")}`;
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;

// Helvetica (WinAnsi) kennt das typografische Apostroph nicht zuverlaessig —
// darum das gerade Apostroph als Tausendertrenner.
const chf = (v) => formatSwissCurrency(v).replace(/[’‘]/g, "'");

const pct = (part, whole) => {
  if (!(num(whole) > 0)) return "";
  return `${Math.round((num(part) / num(whole)) * 100)} %`;
};

const splitLines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

// Aufschluesselung der heutigen Zahlung (Betrag inkl. MWST) in Netto und MWST.
export function berechneMwst(betragBrutto, mwstSatz) {
  const brutto = round2(betragBrutto);
  const satz = num(mwstSatz);
  const netto = satz > 0 ? round2(brutto / (1 + satz / 100)) : brutto;
  return { brutto, netto, mwst: round2(brutto - netto), satz };
}

// Dateiname analog Vorlage: 014_260902_Zahlungsanweisung_1_GP.pdf
export function zahlungsanweisungDateiname(d) {
  const clean = (s) =>
    String(s || "")
      .replace(/[\\/:*?"<>| -]/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^[-.]+|[-.]+$/g, "");
  const parts = [clean(d.projektCode), fmtDatumKurz(d.datum), "Zahlungsanweisung", clean(d.zaNr)];
  if (d.kuerzel) parts.push(clean(d.kuerzel));
  return `${parts.filter(Boolean).join("_")}.pdf`;
}

/**
 * Baut das PDF-Dokument. Gibt die jsPDF-Instanz zurueck (Aufrufer entscheidet
 * ueber save/blob). jsPDF wird erst hier nachgeladen (Code-Splitting).
 */
export async function buildZahlungsanweisungPdf(d) {
  const jsPDF = await loadJsPdf();
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

  const { font, txt, hr, block } = createDrawHelpers(doc);

  // --- Logo -----------------------------------------------------------------
  const logo = await loadLogoDataUrl();
  const logoW = 34;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", RIGHT - logoW, 12, logoW, logoW * LOGO_RATIO);
    } catch {
      /* Logo ist optional */
    }
  }

  // --- Adressat / Auftraggeberin --------------------------------------------
  font("normal", 9);
  const adressatLines = splitLines(d.adressat);
  const agLines = splitLines(d.auftraggeberin);
  let y = 58;
  block(adressatLines, LEFT, y);
  font("bold", 9);
  txt("Auftraggeberin:", MID, y - LH);
  font("normal", 9);
  block(agLines, MID, y);

  // --- Ort, Datum / Projekt -------------------------------------------------
  y = 92;
  font("normal", 9.5);
  txt(`${d.ort || "Basel"}, ${fmtDatum(d.datum)}`, LEFT, y);

  y = 103;
  font("bold", 9.5);
  txt("Projekt:", LEFT, y);
  font("normal", 9.5);
  txt(d.projekt, 46, y);
  if (d.objektgliederung) {
    y += LH + 0.5;
    font("bold", 9.5);
    txt(`Objektgliederung: ${d.objektgliederung}`, 46, y);
  }

  // --- Titel -----------------------------------------------------------------
  y = 124;
  font("bold", 15);
  const titel = `Zahlungsanweisung${d.kuerzel ? ` (${d.kuerzel})` : ""} Nr. ${d.zaNr || ""}`;
  txt(titel, LEFT, y);
  font("bold", 13);
  txt(`vom ${fmtDatum(d.datum)}`, RIGHT, y, { align: "right" });

  // --- Unternehmer -----------------------------------------------------------
  y = 134;
  const unternehmerLines = splitLines(d.unternehmer);
  font("bold", 9.5);
  txt("Unternehmer:", LEFT, y);
  if (unternehmerLines.length) {
    txt(unternehmerLines[0], 48, y);
    font("normal", 9.5);
    y = block(unternehmerLines.slice(1), 48, y + LH);
  } else {
    y += LH;
  }

  y += 3;
  font("normal", 9.5);
  txt("Nachstehender Anweisungsbetrag kann ausbezahlt werden.", LEFT, y);

  // --- Auftrag / Vertrag Nr. ---------------------------------------------------
  y += 6;
  hr(y);
  y += 5.5;
  font("bold", 9.5);
  txt("Auftrag:", LEFT, y);
  font("normal", 9.5);
  txt(d.auftrag, 48, y);
  font("bold", 9.5);
  txt("Vertrag Nr.:", MID, y);
  font("normal", 9.5);
  txt(d.vertragNr || "–", RIGHT, y, { align: "right" });
  y += 3.5;
  hr(y);

  // --- Zahlungsuebersicht (links) + Rechnungsangaben (rechts) -----------------
  const VAL_X = 96; // rechte Kante der Betragsspalte links
  const PCT_X = 113; // rechte Kante der Prozentspalte
  const bisher = num(d.bisherigeZahlungen);
  const heute = num(d.betrag);
  const total = round2(bisher + heute);
  const vertragssumme = num(d.vertragssumme);
  const akontoTotal = round2(num(d.bisherigeAkonto) + (d.istAkonto ? heute : 0));

  y += 8;
  const blockTop = y;
  font("bold", 9.5);
  txt(d.objektgliederung || "Position", LEFT, y);
  font("normal", 9.5);
  txt(d.auftrag, MID, y);

  y += LH + 1;
  font("italic", 9);
  txt("inkl. MWST", VAL_X, y, { align: "right" });

  const row = (label, value, percent, bold = false) => {
    y += LH + 0.7;
    font(bold ? "bold" : "normal", 9.5);
    txt(label, LEFT, y);
    txt(value, VAL_X, y, { align: "right" });
    txt(percent, PCT_X, y, { align: "right" });
  };
  row("Vertragssumme:", chf(vertragssumme), vertragssumme > 0 ? "100 %" : "");
  row("Bisherige Zahlungen:", chf(bisher), pct(bisher, vertragssumme));
  row("Heutige Zahlungen:", chf(heute), "");
  row("Total Zahlungen:", chf(total), pct(total, vertragssumme), true);
  if (d.istAkonto) {
    row("Akonto:", chf(akontoTotal), pct(akontoTotal, vertragssumme));
  } else {
    row("Schlusszahlung:", chf(heute), pct(heute, vertragssumme));
  }
  const leftBottom = y;

  // rechte Spalte, gleiche Zeilenlage wie die Betragszeilen
  let ry = blockTop + LH + 1 + LH + 0.7;
  const rrow = (label, value, wrap = false) => {
    font("normal", 9.5);
    txt(label, MID, ry);
    if (wrap) {
      const lines = doc.splitTextToSize(String(value || ""), RIGHT - MID - 30);
      for (const line of lines) {
        txt(line, RIGHT, ry, { align: "right" });
        ry += LH;
      }
      ry += 0.7;
    } else {
      txt(value || "–", RIGHT, ry, { align: "right" });
      ry += LH + 0.7;
    }
  };
  rrow("Rechn. Nr.:", d.rechnungNr);
  rrow("Buchungstext:", d.buchungstext, true);
  rrow("Datum Rechnung:", d.rechnungsdatum ? fmtDatumLang(d.rechnungsdatum) : "");
  rrow("Datum Fälligkeit:", d.faelligkeit);

  y = Math.max(leftBottom, ry) + 8;

  // --- MWST-Aufschluesselung ---------------------------------------------------
  const { netto, mwst, satz, brutto } = berechneMwst(heute, d.mwstSatz);
  font("normal", 9.5);
  txt(d.istAkonto ? "Akonto" : "Schlusszahlung", LEFT, y);
  txt("Netto (exkl. MWST)", 48, y);
  font("bold", 9.5);
  txt(chf(netto), RIGHT, y, { align: "right" });
  y += LH + 0.7;
  font("normal", 9.5);
  txt("MWST", 48, y);
  txt(`${String(satz).replace(".", ",")} %`, 150, y, { align: "right" });
  txt(chf(mwst), RIGHT, y, { align: "right" });
  y += 1.6;
  doc.setLineWidth(0.3);
  doc.line(RIGHT - 42, y, RIGHT, y);
  y += LH;
  font("bold", 9.5);
  txt("Zahlung (inkl. MWST)", 48, y);
  txt(chf(brutto), RIGHT, y, { align: "right" });
  y += 1.6;
  doc.setLineWidth(0.5);
  doc.line(RIGHT - 42, y, RIGHT, y);

  // --- Unterschriften ------------------------------------------------------------
  y += 8;
  hr(y);
  y += 6;
  font("normal", 9.5);
  txt(`${d.ort || "Basel"}, ${fmtDatum(d.datum)}`, LEFT, y);
  y += LH + 0.5;
  font("bold", 9.5);
  txt(MF_FIRMA, LEFT, y);

  y += 17;
  doc.setLineWidth(0.3);
  doc.line(LEFT, y, LEFT + 52, y);
  doc.line(LEFT + 62, y, LEFT + 114, y);
  y += 4.5;
  font("normal", 8.5);
  const sig = (s) => (s?.name ? `${s.name}${s.funktion ? `, ${s.funktion}` : ""}` : "");
  txt(sig(d.unterschrift1), LEFT, y);
  txt(sig(d.unterschrift2), LEFT + 62, y);

  // --- Fusszeile ---------------------------------------------------------------
  font("normal", 8);
  doc.setTextColor(70);
  txt(MF_ABSENDER, 105, 286, { align: "center" });
  doc.setTextColor(0);

  doc.setProperties({
    title: titel,
    subject: d.projekt || "",
    author: MF_FIRMA,
    creator: `${MF_FIRMA} INTRA`,
  });

  return doc;
}

// PDF erzeugen und im Browser als Download ausloesen.
export async function downloadZahlungsanweisungPdf(d) {
  const doc = await buildZahlungsanweisungPdf(d);
  doc.save(zahlungsanweisungDateiname(d));
}
